import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth, useApi } from '@/context/AuthContext';
import { formatIndianCurrency, formatDate, getStatusClasses } from '@/utils/helpers';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  FileText,
  Users,
  Building2,
  Receipt,
  Plus,
  ArrowRight,
  AlertTriangle,
  IndianRupee
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export default function DashboardPage() {
  const { user, business } = useAuth();
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchDashboardStats = async () => {
      try {
        setError(null);
        const response = await api.get('/dashboard/stats');
        if (isMounted) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        if (isMounted) {
          setError('Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchDashboardStats();
    
    return () => {
      isMounted = false;
    };
  }, [api]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-stone-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-80 bg-stone-200 rounded-lg"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatIndianCurrency(stats?.total_revenue || 0),
      icon: IndianRupee,
      subtext: 'Collected from invoices',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Outstanding Receivables',
      value: formatIndianCurrency(stats?.outstanding_receivables || 0),
      icon: FileText,
      subtext: `${stats?.open_invoice_count || 0} open invoice${stats?.open_invoice_count === 1 ? '' : 's'}`,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Total Expenses',
      value: formatIndianCurrency(stats?.total_expenses || 0),
      icon: Receipt,
      subtext: `${stats?.expense_count || 0} expense${stats?.expense_count === 1 ? '' : 's'}`,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      title: 'Cash Position',
      value: formatIndianCurrency(stats?.cash_position || 0),
      icon: TrendingUp,
      change: stats?.cash_position >= 0 ? 'Healthy' : 'Low',
      positive: stats?.cash_position >= 0,
      color: stats?.cash_position >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: stats?.cash_position >= 0 ? 'bg-green-50' : 'bg-red-50'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">
              {stats && !stats.invoice_count && !stats.expense_count ? 'Welcome to Laksh' : 'Welcome back'}, {user?.name?.split(' ')[0] || 'there'}
            </h1>
            <p className="text-stone-600">Here's what's happening with your business today.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/invoices/new">
              <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press gap-2" data-testid="create-invoice-btn">
                <Plus className="w-4 h-4" /> New Invoice
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="bento-card" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{stat.title}</p>
                    <p className="text-2xl font-bold font-mono text-stone-900">{stat.value}</p>
                    {stat.change && (
                      <p className={`text-sm mt-1 flex items-center gap-1 ${stat.positive ? 'text-green-600' : 'text-red-600'}`}>
                        {stat.positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {stat.change}
                      </p>
                    )}
                    {stat.subtext && (
                      <p className="text-sm text-stone-500 mt-1">{stat.subtext}</p>
                    )}
                  </div>
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts and Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <Card className="bento-card lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-stone-900 font-['Outfit']">Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64" data-testid="revenue-chart">
                {stats?.revenue_by_month && stats.revenue_by_month.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.revenue_by_month}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4338ca" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#4338ca" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const parts = value?.split('-');
                          if (parts && parts.length === 2) {
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return months[parseInt(parts[1]) - 1] || value;
                          }
                          return value;
                        }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value >= 100000 ? `₹${(value / 100000).toFixed(1)}L` : value >= 1000 ? `₹${(value / 1000).toFixed(0)}K` : `₹${value}`}
                        allowDecimals={false}
                        domain={[0, (max) => Math.max(max, 1000)]}
                      />
                      <Tooltip 
                        formatter={(value) => [formatIndianCurrency(value), 'Revenue']}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#4338ca" 
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-stone-400">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No revenue data yet</p>
                      <p className="text-sm">Create your first invoice to see trends</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-stone-900 font-['Outfit']">Quick Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#1D9E75]" />
                  <span className="text-stone-700">Customers</span>
                </div>
                <span className="font-semibold font-mono text-stone-900">{stats?.customer_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-[#1D9E75]" />
                  <span className="text-stone-700">Vendors</span>
                </div>
                <span className="font-semibold font-mono text-stone-900">{stats?.vendor_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#1D9E75]" />
                  <span className="text-stone-700">Total Invoices</span>
                </div>
                <span className="font-semibold font-mono text-stone-900">{stats?.invoice_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-[#1D9E75]" />
                  <span className="text-stone-700">Total Expenses</span>
                </div>
                <span className="font-semibold font-mono text-stone-900">{stats?.expense_count || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Invoices */}
          <Card className="bento-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-stone-900 font-['Outfit']">Recent Invoices</CardTitle>
              <Link to="/invoices" className="text-sm text-[#1D9E75] hover:text-[#178a65] flex items-center gap-1">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_invoices?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_invoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-3 border border-stone-100 rounded-lg hover:bg-stone-50 transition-colors">
                      <div>
                        <p className="font-medium text-stone-900">{invoice.customer_name}</p>
                        <p className="text-sm text-stone-500">{invoice.reference_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold">{formatIndianCurrency(invoice.amount + invoice.tax_amount)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusClasses(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p>No invoices yet</p>
                  <Link to="/invoices/new">
                    <Button variant="link" className="text-[#1D9E75]">Create your first invoice</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overdue Invoices */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-stone-900 font-['Outfit'] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Overdue Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.overdue_invoices?.length > 0 ? (
                <div className="space-y-3">
                  {stats.overdue_invoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg">
                      <div>
                        <p className="font-medium text-stone-900">{invoice.customer_name}</p>
                        <p className="text-sm text-red-600">Due: {formatDate(invoice.due_date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold text-red-700">{formatIndianCurrency(invoice.amount + invoice.tax_amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-green-700 font-medium">All caught up!</p>
                  <p className="text-sm text-stone-500">No overdue invoices</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
